import * as React from "react"
import { router } from "@inertiajs/react"
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogClose,
} from "@/components/ui/Dialog"
import { Button } from "@/components/ui/Button"
import WorkplaceListbox from "@/components/WorkplaceListbox"
import RoleCardList, { type RoleCard } from "@/components/RoleCardList"
import { deriveWorkplaces, type OrgTreeNode } from "@/lib/orgTree"

type Props = {
  userId: string
  roles: RoleCard[]
  nodes: OrgTreeNode[]
  existing: Array<{ role_id: string; node_id: string }>
  // Prefill (e.g. approving an onboarding request "as requested") — still editable.
  defaultRoleId?: string | null
  defaultNodeId?: string | null
  trigger?: React.ReactNode
}

// Grant a role at an org node. Mirrors the onboarding wizard's pickers
// (WorkplaceListbox + RoleCardList) so requesting and granting access look and
// feel the same. Combinations the user already holds are disabled.
export default function AssignRoleDialog({ userId, roles, nodes, existing, defaultRoleId, defaultNodeId, trigger }: Props) {
  const [open, setOpen] = React.useState(false)
  const [roleId, setRoleId] = React.useState<string | null>(defaultRoleId ?? null)
  const [nodeId, setNodeId] = React.useState<string | null>(defaultNodeId ?? null)
  const [submitting, setSubmitting] = React.useState(false)

  const workplaces = React.useMemo(() => deriveWorkplaces(nodes), [nodes])

  const taken = React.useMemo(
    () => new Set(existing.map((e) => `${e.role_id}::${e.node_id}`)),
    [existing]
  )
  const disabledRoleIds = React.useMemo(() => {
    if (!nodeId) return undefined
    return new Set(roles.filter((r) => taken.has(`${r.id}::${nodeId}`)).map((r) => r.id))
  }, [roles, taken, nodeId])

  const submit = () => {
    if (!roleId || !nodeId) return
    setSubmitting(true)
    router.post(
      "/user_roles",
      { user_role: { user_id: userId, role_id: roleId, org_node_id: nodeId } },
      {
        onFinish: () => {
          setSubmitting(false)
          setOpen(false)
          setRoleId(defaultRoleId ?? null)
          setNodeId(defaultNodeId ?? null)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? <Button variant="outline" size="sm">Assign role</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign role</DialogTitle>
        </DialogHeader>

        <div className="-mx-1 flex max-h-[60vh] flex-col gap-6 overflow-y-auto px-1">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-foreground">Where</h3>
            <WorkplaceListbox workplaces={workplaces} selectedId={nodeId} onSelect={setNodeId} />
            <p className="text-xs text-muted-foreground">Permissions cascade down from the selected node.</p>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium text-foreground">Role</h3>
            <RoleCardList
              roles={roles}
              selectedId={roleId}
              onSelect={setRoleId}
              disabledIds={disabledRoleIds}
            />
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Cancel</Button></DialogClose>
          <Button disabled={!roleId || !nodeId || submitting} onClick={submit}>Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
